import { ChevronRight, LogIn } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'

import { Clipboard } from '@/components/Clipboard'
import { KeyValueList, StatusBadge } from '@/components/ds'
import { Button } from '@/components/ui/button'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { AdminSection } from '@/containers/AdminAccounts/AdminSection'
import { useAdminUserImpersonation } from '@/hooks/useAdminUserImpersonation'
import { AdminUser } from '@/types/User'

import { RoleBadge } from './RoleBadge'

export function AdminUserDetailView({ user }: { user: AdminUser }) {
  const { t } = useTranslation(undefined, {
    keyPrefix: 'admin.users',
  })
  const navigate = useNavigate()
  const { impersonate, impersonatingUserId } = useAdminUserImpersonation()

  const currentAccountId = (user as AdminUser & { currentAccountId?: string }).currentAccountId

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0 space-y-1">
          <div className="flex items-center gap-3">
            <h1 className="truncate text-2xl font-medium tracking-[-0.02em]">{user.fullName || user.email}</h1>
            {user.verified ? (
              <StatusBadge status="paid" label={t('detail.profile.verified')} />
            ) : (
              <StatusBadge status="pending" label={t('detail.profile.unverified')} />
            )}
          </div>
          <p className="text-sm text-muted-foreground">{user.email}</p>
        </div>
        <Button disabled={impersonatingUserId === user.id} onClick={() => impersonate(user.id)}>
          <LogIn />
          {t('list.actions.impersonate')}
        </Button>
      </div>

      <div className="grid gap-8 lg:grid-cols-[minmax(0,320px)_minmax(0,1fr)]">
        <AdminSection title={t('detail.profile.title')}>
          <KeyValueList
            items={[
              {
                label: 'ID',
                value: (
                  <Clipboard value={String(user.id)}>
                    <span className="font-mono text-xs">{user.id}</span>
                  </Clipboard>
                ),
              },
              { label: 'Jméno', value: user.fullName || '-' },
              { label: 'E-mail', value: user.email },
              {
                label: 'Role',
                value: user.roles.length ? user.roles.map((role) => t(`roles.${role}`)).join(', ') : '-',
              },
              { label: 'Účty', value: user.accounts.length },
            ]}
          />
        </AdminSection>

        <AdminSection title={t('detail.accounts.title')} description="Účty, ke kterým má uživatel přístup">
          {user.accounts.length === 0 ? (
            <div className="rounded-lg border border-dashed px-6 py-8 text-center text-sm text-muted-foreground">
              Uživatel nepatří k žádnému účtu.
            </div>
          ) : (
            <div className="overflow-hidden rounded-lg border">
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent">
                    <TableHead>{t('detail.accounts.account')}</TableHead>
                    <TableHead>{t('detail.accounts.roles')}</TableHead>
                    <TableHead className="w-10" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {user.accounts.map((account) => (
                    <TableRow
                      key={account.id}
                      onClick={() => navigate(`/admin/accounts/${account.accountId}`)}
                      className="group cursor-pointer"
                    >
                      <TableCell className="max-w-0">
                        <div className="flex items-center gap-2">
                          <p className="truncate font-medium">{account.accountName || account.accountId}</p>
                          {currentAccountId === account.accountId && <RoleBadge muted>Aktuální</RoleBadge>}
                        </div>
                        <p className="truncate font-mono text-xs text-muted-foreground">{account.accountId}</p>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {account.roles.map((role) => (
                            <RoleBadge key={role}>{t(`roles.${role}`)}</RoleBadge>
                          ))}
                          {!account.verified && <RoleBadge muted>{t('list.state.unverified')}</RoleBadge>}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <ChevronRight className="ml-auto size-4 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </AdminSection>
      </div>
    </div>
  )
}
